<template>
  <div class="ctw-field">
    <label
      v-if="hasLabel"
      :for="fieldId"
      class="ctw-field__label"
    >
      <slot name="label">{{ label }}</slot>
      <span v-if="required" class="ctw-field__required" aria-hidden="true">*</span>
    </label>
    <input
      :id="fieldId"
      :class="inputClass"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :value="value"
      v-bind="inputAttrs"
      @input="onInput"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
  </div>
</template>

<script>
export default {
  name: "CTailwindInput",
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    /** Mirrors `w-full` from new_design (default true). */
    fullWidth: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasLabel() {
      return Boolean(this.label) || Boolean(this.$slots.label);
    },
    fieldId() {
      return this.id || `ctw-input-${this._uid}`;
    },
    inputClass() {
      return [
        "ctw-input",
        { "ctw-input--full": this.fullWidth, "ctw-input--error": this.error },
        this.$attrs.class,
      ];
    },
    inputAttrs() {
      const rest = { ...this.$attrs };
      delete rest.class;
      return rest;
    },
  },
  methods: {
    onInput(e) {
      this.$emit("input", e.target.value);
    },
  },
};
</script>

<style scoped>
/* Label — block text-sm font-medium mb-1 (Cart2) */
.ctw-field__label {
  display: block;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  margin-bottom: calc(var(--spacing) * 1);
  color: var(--color-gray-800);
}

html.dark .ctw-field__label {
  color: var(--color-gray-100);
}

.ctw-field__required {
  color: var(--color-red-500);
  margin-left: 0.125rem;
}

/*
  form-input w-full — unlayered so Vuetify input resets do not win over @layer utilities.
  Aligned with utility-patterns .form-input + tailwind.generated form-input rules.
*/
.ctw-input {
  box-sizing: border-box;
  display: block;
  min-width: 0;
  margin: 0;
  border-style: solid;
  border-width: 1px;
  border-radius: var(--radius-lg);
  border-color: var(--color-gray-200);
  background-color: var(--color-white);
  padding-inline: calc(var(--spacing) * 3);
  padding-block: calc(var(--spacing) * 2);
  font-family: var(--font-inter), var(--font-sans);
  font-size: var(--text-sm);
  line-height: calc(var(--spacing) * 5);
  color: var(--color-gray-800);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition-property: border-color, box-shadow, background-color, color, opacity;
  transition-duration: var(--default-transition-duration);
  transition-timing-function: var(--default-transition-timing-function);
}

.ctw-input--full {
  width: 100%;
  max-width: 100%;
}

.ctw-input::placeholder {
  color: var(--color-gray-400);
}

.ctw-input:hover:not(:disabled) {
  border-color: var(--color-gray-300);
}

.ctw-input:focus {
  outline: none;
  border-color: var(--color-gray-300);
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}

.ctw-input--error {
  border-color: var(--color-red-500) !important;
}

.ctw-input--error:focus {
  border-color: var(--color-red-500) !important;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-red-500) 35%, transparent);
}

html.dark .ctw-input {
  background-color: color-mix(in srgb, var(--color-gray-900) 30%, transparent);
  border-color: color-mix(in srgb, var(--color-gray-700) 60%, transparent);
  color: var(--color-gray-100);
}

html.dark .ctw-input::placeholder {
  color: var(--color-gray-500);
}

html.dark .ctw-input:hover:not(:disabled) {
  border-color: var(--color-gray-600);
}

html.dark .ctw-input:focus {
  border-color: var(--color-gray-600);
}

html.dark .ctw-input--error {
  border-color: var(--color-red-400) !important;
}

html.dark .ctw-input--error:focus {
  border-color: var(--color-red-400) !important;
}

.ctw-input:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

html.dark .ctw-input:disabled {
  border-color: var(--color-gray-700);
  background-color: color-mix(in srgb, var(--color-gray-700) 30%, transparent);
}

html.dark .ctw-input:disabled:hover {
  border-color: var(--color-gray-700);
}
</style>
